import { comemorar } from '../../design/Comemoracoes';
import type {
  ChaveDoTicket,
  JanelaDoDesempenho,
  MetaDoTicket,
  MeuPainel,
  ResumoDoBalconista,
} from '../../vendas';
import { metaDaSemana } from './meta-e-bloqueio';

/**
 * MARCOS — quando comemorar (dono, 25/09; o 1.0 fazia parecido). Guarda NESTE APARELHO o último
 * ponto que a pessoa viu e comemora só quando ela PASSA de um degrau: nunca repete, e a primeira
 * vez que o aparelho vê a semana só anota (não "comemora" o que já tinha acontecido — como no 1.0).
 *   - Meta obrigatória da semana: aviso em 50%, 75% e 90%; tela cheia em 100%.
 *   - Metas do mês (+5/+10/+15% por ticket): tela cheia a cada degrau novo.
 *   - Ranking: aviso quando a posição melhora.
 */

const ler = (k: string): string | null => {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
};
const gravar = (k: string, v: string) => {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    // sem armazenamento: só não comemora de novo
  }
};

/**
 * A meta da semana conta os itens CONFIRMADOS (os do cheque), não os lançados — "META DA SEMANA
 * BATIDA!" com o lançado fazia a pessoa comemorar e receber R$ 0 (revisão 360, D6).
 */
export function marcosDaSemana(login: string, r: ResumoDoBalconista): void {
  const m = metaDaSemana(r);
  if (m.meta <= 0) return;
  // chave nova (".cheque"): a antiga guardava o % do LANÇADO e travaria a comemoração verdadeira
  const chave = `inovapay2.marco.${login}.semana.${r.semana.de}.cheque`;
  const pct = m.batida ? 100 : Math.min(99, m.pct);
  const antes = ler(chave);
  gravar(chave, String(Math.max(pct, Number(antes ?? 0))));
  if (antes === null) return;
  const ultimo = Number(antes);
  const passou = [100, 90, 75, 50].find((t) => ultimo < t && pct >= t);
  if (passou === undefined) return;
  const faltam = m.faltam;
  if (passou === 100)
    comemorar({
      tipo: 'grande',
      titulo: 'META DA SEMANA BATIDA!',
      texto: `${String(m.confirmados)} de ${String(m.meta)} itens confirmados com o HOS. Parabéns! Agora cada venda confirmada é premiação garantida.`,
    });
  else
    comemorar({
      tipo: 'aviso',
      titulo: `${String(passou)}% da meta da semana!`,
      texto: `Faltam ${String(faltam)} ${faltam === 1 ? 'item confirmado' : 'itens confirmados'} até sexta. Você consegue!`,
    });
}

const NOMES: Record<ChaveDoTicket, string> = {
  geral: 'ticket médio geral',
  itensPorCupom: 'itens por cupom',
  produtosPorCupom: 'produtos diferentes por cupom',
  porVenda: 'ticket por venda no InovaPay',
  vitamina: 'ticket da vitamina',
  preVencido: 'ticket do pré-vencido',
  paradao: 'ticket do paradão',
  entrega: 'ticket de entrega',
};

export function marcosDoMes(login: string, p: MeuPainel): void {
  if (!p.metas) return;
  const mes = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);
  const novas: { nome: string; pct: number }[] = [];
  for (const [k, m] of Object.entries(p.metas) as [ChaveDoTicket, MetaDoTicket][]) {
    if (m.situacao !== 'com_base') continue;
    const chave = `inovapay2.marco.${login}.mes.${mes}.${k}`;
    const antes = Number(ler(chave) ?? 0);
    if (m.batidas > antes) {
      novas.push({ nome: NOMES[k], pct: m.alvos[m.batidas - 1]?.pct ?? 0 });
      gravar(chave, String(m.batidas));
    }
  }
  const melhor = novas.sort((a, b) => b.pct - a.pct)[0];
  if (!melhor) return;
  comemorar({
    tipo: 'grande',
    titulo: `+${String(melhor.pct)}% NO ${melhor.nome.toUpperCase()}!`,
    texto:
      melhor.pct === 15
        ? 'Você bateu as 3 metas: 15% acima do seu mês passado. Isso é vender bem!'
        : `Meta do mês batida${novas.length > 1 ? ` (e mais ${String(novas.length - 1)})` : ''}. A próxima é mais 5% — bora!`,
  });
}

const RANKINGS: readonly { k: keyof MeuPainel['rankings']; nome: string }[] = [
  { k: 'vendas', nome: 'nas vendas' },
  { k: 'vitamina', nome: 'em vitaminas' },
  { k: 'preVencido', nome: 'em pré-vencidos' },
  { k: 'paradao', nome: 'no paradão' },
  { k: 'entregas', nome: 'em entregas' },
];
const QUANDO: Record<JanelaDoDesempenho, string> = {
  hoje: 'hoje',
  semana: 'na semana',
  mes: 'no mês',
};

export function marcosDoRanking(login: string, p: MeuPainel): void {
  const subiu: string[] = [];
  for (const { k, nome } of RANKINGS) {
    const pos = p.rankings[k].posicao;
    if (pos === null) continue;
    const chave = `inovapay2.marco.${login}.rank.${p.janela}.${p.de}.${k}`;
    const antes = ler(chave);
    gravar(chave, String(pos));
    if (antes !== null && pos < Number(antes)) subiu.push(`${String(pos)}º ${nome}`);
  }
  if (subiu.length === 0) return;
  comemorar({
    tipo: 'aviso',
    titulo: 'Você subiu no ranking!',
    texto: `Agora você está em ${subiu.join(', ')} ${QUANDO[p.janela]}.`,
  });
}
